import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LanguageIcon from '@mui/icons-material/Language';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';

import { useAuth } from '@/components/auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';
import { useDirection } from '@/hooks/useDirection';
import useSettingsSections from '@/hooks/useSettingsSections';
import { useSettingsLeaveGuard } from '@/hooks/useSettingsLeaveGuard';
import { useSettingsRouteGuard } from '@/hooks/useSettingsRouteGuard';
import { BORDER_RADIUS, semanticColors, spacingTokens, typographyTokens } from '@/theme/tokens';
import BallotIcon from '@/ui-library/BallotIcon';
import { getInputModality, installInputModalityTracker } from '@/utils/inputModality';
import {
  SETTINGS_SECTIONS,
  type SettingsEditState,
  type SettingsSectionId,
  type SettingsSectionRequest,
} from './settingsTypes';
import { SettingsSurfaceProvider } from './SettingsSection';
import SettingsDiscardDialog from './SettingsDiscardDialog';
import AnswerStyleSettingsSection from './AnswerStyleSettingsSection';
import { LanguageSettingsSection, NavigationSettingsSection } from './GeneralSettingsSections';
import {
  ProfileSettingsSection,
  NameSettingsSection,
  DeleteAccountSection,
} from './AccountSettingsSections';
import {
  SourcesSettingsSection,
  AiDataSettingsSection,
  LegalPrivacySettingsSection,
} from './PrivacySettingsSections';
import BallotSettingsSection from './BallotSettingsSection';
import NotificationSettingsSection from './NotificationSettingsSection';
import MembershipSettingsSection from './MembershipSettingsSection';
import SettingsLoadingState from './SettingsLoadingState';

const ICON_SX = { fontSize: 18 };
// Ballots uses the same ballot glyph as the mobile app and the desktop ballot pane.
const SECTION_ICONS = {
  general: <LanguageIcon sx={ICON_SX} />,
  response: <ChatBubbleOutlineIcon sx={ICON_SX} />,
  ballots: <BallotIcon size={18} />,
  notifications: <NotificationsNoneIcon sx={ICON_SX} />,
  membership: <FavoriteBorderIcon sx={ICON_SX} />,
  privacy: <ShieldOutlinedIcon sx={ICON_SX} />,
  account: <PersonOutlineIcon sx={ICON_SX} />,
};

export default function SettingsDialog({
  open,
  initialSection = 'general',
  sectionRequest,
  onClose,
  removedAccountId = null,
  onAccountRemoved,
}: {
  open: boolean;
  initialSection?: SettingsSectionId;
  sectionRequest?: SettingsSectionRequest;
  onClose: () => void;
  removedAccountId?: string | null;
  onAccountRemoved?: (id: string) => void;
}) {
  const { t } = useTranslation('settings');
  const { user, isInitializing, signOut } = useAuth();
  const analytics = useAnalytics();
  const direction = useDirection();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const overlayTheme = useMemo(
    () =>
      createTheme({
        ...theme,
        // The app uses a 16px base; this compact dialog uses the 8px scale.
        spacing: spacingTokens.sm,
        zIndex: { ...theme.zIndex, modal: 2100 },
      }),
    [theme]
  );
  const [section, setSection] = useState(initialSection);
  const [privacyRequested, setPrivacyRequested] = useState(initialSection === 'privacy');
  const [privacyArrival, setPrivacyArrival] = useState(0);
  const handledRequest = useRef(sectionRequest);
  const [showMobileContent, setShowMobileContent] = useState(true);
  const [answerEdit, setAnswerEdit] = useState<SettingsEditState | null>(null);
  const [operationBusy, setOperationBusy] = useState(false);
  const settings = useSettingsSections();
  const accountId = user?.user_id ?? user?.id ?? user?.auth0Id ?? null;
  const accountRemoved = !!removedAccountId && removedAccountId === accountId;
  const { loadUserSettings } = settings;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const requestedSettings = useRef(false);
  const trackedSection = useRef<SettingsSectionId | null>(null);
  const identity = useRef(user?.user_id ?? user?.id ?? null);
  const closed = useRef(false);
  const deletionPending = useRef(false);
  const reportDeletionBusy = useCallback((saving: boolean) => {
    deletionPending.current = saving;
    setOperationBusy(saving);
  }, []);
  const trackAndClose = useCallback(
    (reason: string) => {
      if (closed.current) return;
      closed.current = true;
      analytics.track('Settings Closed', { section, reason, surface: 'dialog' });
      onClose();
    },
    [analytics, onClose, section]
  );

  useEffect(() => installInputModalityTracker(), []);
  useEffect(() => {
    if (!isInitializing && user && !accountRemoved && !requestedSettings.current) {
      requestedSettings.current = true;
      void loadUserSettings();
    }
  }, [isInitializing, user, accountRemoved, loadUserSettings]);
  useEffect(() => {
    if (isInitializing) return;
    const next = user?.user_id ?? user?.id ?? null;
    if (next !== identity.current)
      trackAndClose(deletionPending.current ? 'account_deleted' : 'identity_changed');
  }, [isInitializing, user, trackAndClose]);
  useEffect(() => {
    if (trackedSection.current === section) return;
    trackedSection.current = section;
    analytics.track('Settings Section Viewed', { section, surface: 'dialog', authed: !!user });
  }, [analytics, section, user]);

  const groups = settings.groups;
  const dirty =
    (user && (groups.name.dirty || groups.address.dirty || groups.phone.dirty)) ||
    answerEdit?.dirty;
  const busy =
    operationBusy ||
    !!answerEdit?.saving ||
    Object.values(groups).some((group) => group.state === 'saving');
  const discard = useCallback(() => {
    if (groups.name.dirty) settings.cancelName();
    if (groups.address.dirty) settings.cancelAddress();
    if (groups.phone.dirty) settings.cancelPhone();
    if (answerEdit?.dirty) answerEdit.discard();
  }, [groups, settings, answerEdit]);
  const leave = useSettingsLeaveGuard({ dirty: !!dirty, busy, onDiscard: discard });
  const routeGuard = useSettingsRouteGuard({
    dirty: !!dirty,
    busy,
    requestLeave: leave.requestLeave,
    onLocationChange: () => trackAndClose('navigation'),
  });
  const close = (reason: string) =>
    leave.requestLeave(() => {
      trackAndClose(reason);
    });
  const { requestLeave } = leave;
  const selectSection = useCallback(
    (next: SettingsSectionId, external = false) => {
      if (accountRemoved && next !== 'account') return;
      if (next === section) {
        setPrivacyRequested(external && next === 'privacy');
        if (external && next === 'privacy') setPrivacyArrival((value) => value + 1);
        setShowMobileContent(true);
        return;
      }
      requestLeave(() => {
        setSection(next);
        setPrivacyRequested(external && next === 'privacy');
        if (external && next === 'privacy') setPrivacyArrival((value) => value + 1);
        setAnswerEdit(null);
        setShowMobileContent(true);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        requestAnimationFrame(() => {
          if (getInputModality() === 'keyboard' && !(external && next === 'privacy'))
            headingRef.current?.focus();
        });
      });
    },
    [accountRemoved, section, requestLeave]
  );
  useEffect(() => {
    if (!sectionRequest || handledRequest.current === sectionRequest) return;
    // Consume requests once: cancellation or a busy save must not replay them later.
    handledRequest.current = sectionRequest;
    selectSection(sectionRequest.section, true);
  }, [sectionRequest, selectSection]);
  const guardedSignOut = () =>
    leave.requestLeave(() => {
      trackAndClose('sign_out');
      void signOut();
    });

  const accountPrompt = <ProfileSettingsSection />;
  const content = (() => {
    switch (section) {
      case 'general':
        return (
          <>
            <LanguageSettingsSection settings={settings} />
            <NavigationSettingsSection settings={settings} />
          </>
        );
      case 'response':
        return (
          <>
            <AnswerStyleSettingsSection onEditStateChange={setAnswerEdit} />
            <SourcesSettingsSection />
          </>
        );
      case 'ballots':
        return user ? (
          <BallotSettingsSection settings={settings} onBusyChange={setOperationBusy} />
        ) : (
          accountPrompt
        );
      case 'notifications':
        return user ? <NotificationSettingsSection settings={settings} /> : accountPrompt;
      case 'membership':
        return <MembershipSettingsSection onBeforeOpen={() => trackAndClose('membership')} />;
      case 'privacy':
        return (
          <>
            <LegalPrivacySettingsSection
              settings={settings}
              focusPrivacy={privacyRequested}
              privacyArrival={privacyArrival}
            />
            <AiDataSettingsSection />
          </>
        );
      case 'account':
        return (
          <>
            {!accountRemoved && <ProfileSettingsSection onSignOut={guardedSignOut} />}
            {user && (
              <>
                {!accountRemoved && <NameSettingsSection settings={settings} />}
                <DeleteAccountSection
                  onDeleted={() => trackAndClose('account_deleted')}
                  onBusyChange={reportDeletionBusy}
                  onRequestDelete={leave.requestLeave}
                  accountAlreadyRemoved={accountRemoved}
                  onAccountRemoved={() => {
                    if (accountId) onAccountRemoved?.(accountId);
                  }}
                />
              </>
            )}
          </>
        );
    }
  })();

  return (
    <ThemeProvider theme={overlayTheme}>
      <SettingsSurfaceProvider surface="dialog">
        <Dialog
          open={open}
          onClose={(_, reason) => close(reason)}
          fullScreen={smallScreen}
          maxWidth={false}
          disableRestoreFocus
          aria-labelledby="settings-dialog-title"
          slotProps={{
            paper: {
              dir: direction,
              sx: {
                width: { md: 'min(1000px, calc(100vw - 64px))' },
                height: { xs: '100dvh', md: 'min(640px, calc(100dvh - 64px))' },
                maxHeight: { xs: '100dvh', md: 'calc(100dvh - 64px)' },
                m: { xs: 0, md: 4 },
                borderRadius: { xs: 0, md: BORDER_RADIUS },
                backgroundColor: semanticColors.surface,
                overflow: 'hidden',
                // FocusTrap may focus the paper; focus rings belong on the controls.
                outline: 'none',
                '& .MuiButtonBase-root.Mui-focusVisible': {
                  outline: `2px solid ${semanticColors.brandPrimary}`,
                  outlineOffset: '-2px',
                },
                '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: semanticColors.brandPrimary,
                    borderWidth: 2,
                  },
                '& .MuiInputLabel-root.Mui-focused:not(.Mui-error)': {
                  color: semanticColors.textBrand,
                },
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
            <Box
              component="nav"
              aria-label={t('navigationTitle')}
              sx={{
                width: { xs: '100%', md: 224 },
                flexShrink: 0,
                borderInlineEnd: { md: `1px solid ${semanticColors.borderLight}` },
                backgroundColor: semanticColors.surfaceSubtle,
                p: 1.5,
                overflowY: 'auto',
                display: { xs: showMobileContent ? 'none' : 'block', md: 'block' },
              }}
            >
              <Typography
                sx={{
                  px: 1.5,
                  pt: 2,
                  pb: 2.5,
                  fontSize: typographyTokens.settingsLabel.fontSize,
                  fontWeight: 600,
                }}
              >
                {t('title')}
              </Typography>
              <List disablePadding>
                {SETTINGS_SECTIONS.map((id) => {
                  return (
                    <ListItemButton
                      key={id}
                      selected={section === id}
                      disabled={accountRemoved && id !== 'account'}
                      onClick={() => selectSection(id)}
                      aria-current={section === id ? 'page' : undefined}
                      sx={{
                        borderRadius: BORDER_RADIUS,
                        mb: 0.5,
                        px: 1.5,
                        py: 1,
                        minHeight: 44,
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: semanticColors.overlaySelected,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, color: semanticColors.textPrimary }}>
                        {SECTION_ICONS[id]}
                      </ListItemIcon>
                      <ListItemText
                        primary={t(`sections.${id}`)}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: typographyTokens.body2.fontSize,
                              fontWeight: section === id ? 600 : 400,
                            },
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: { xs: 2, md: 4 },
                  py: 2,
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {smallScreen && showMobileContent && (
                    <IconButton
                      aria-label={t('backToSections')}
                      onClick={() => leave.requestLeave(() => setShowMobileContent(false))}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Typography
                    ref={headingRef}
                    tabIndex={-1}
                    id="settings-dialog-title"
                    component="h1"
                    sx={{ fontSize: typographyTokens.heading4.fontSize, fontWeight: 600 }}
                  >
                    {t(showMobileContent ? `sections.${section}` : 'title')}
                  </Typography>
                </Box>
                <IconButton
                  aria-label={t('closeSettings')}
                  onClick={() => close('close_button')}
                  disabled={busy}
                  sx={{ width: 44, height: 44 }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
                {busy ? t('savingBeforeLeaving') : ''}
              </Box>
              <Box
                ref={scrollRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  scrollbarGutter: 'stable',
                  overflowAnchor: 'none',
                  px: { xs: 2, md: 4 },
                  pb: 4,
                  display: { xs: showMobileContent ? 'block' : 'none', md: 'block' },
                }}
              >
                {isInitializing ||
                (section !== 'privacy' &&
                  !accountRemoved &&
                  user &&
                  (!settings.settingsLoaded || settings.settingsLoadError)) ? (
                  <SettingsLoadingState
                    error={settings.settingsLoadError}
                    onRetry={() => void settings.loadUserSettings()}
                  />
                ) : (
                  content
                )}
              </Box>
            </Box>
          </Box>
        </Dialog>
        <SettingsDiscardDialog
          open={leave.confirmationOpen}
          busy={busy}
          onKeep={() => {
            leave.keepEditing();
            routeGuard.resetBlockedNavigation();
          }}
          onDiscard={leave.discardAndLeave}
        />
      </SettingsSurfaceProvider>
    </ThemeProvider>
  );
}
